import type {
  PlansResponse,
  CreateOrderResponse,
  AgenticCartBundleSummaryResponse,
  CreateAgenticCartOrderResponse,
  VerifyAgenticCartPaymentResponse,
  CreateSubscriptionResponse,
  VerifySubscriptionResponse,
} from "@ishvaram/api-client";
import { getAnalyticsDistinctId, getGoogleAnalyticsClientId, identifyUser } from "@/lib/analytics";
import { getGoogleAnalyticsSessionId } from "@/lib/analytics-ga4";
import type { CheckoutChannel } from "@/lib/checkout-log-domain";
import type { CommerceSystem } from "@/lib/commerce-routing";
import { getTurnstileToken } from "@/lib/turnstile";
import { API_BASE, apiRequest, authJsonHeaders } from "./_fetch";

export type { PlansResponse, CreateOrderResponse, CreateSubscriptionResponse };
export type { CreditPack, PremiumReport, Subscription, Session } from "@ishvaram/api-client";

/**
 * Page context at the moment a server-canonical payment call fires.
 * Forwarded to the backend so GA4 Measurement Protocol can attribute
 * begin_checkout / purchase / checkout_abandoned / payment_failed
 * events to the buyer's actual URL — otherwise GA4 Pages / Landing /
 * Monetization reports show "(not set)" for every backend-fired event.
 */
function pageContext(): {
  page_location: string;
  page_title: string;
  page_referrer: string;
  ga_client_id: string;
  ga_session_id: string;
} {
  if (typeof window === "undefined") {
    return {
      page_location: "",
      page_title: "",
      page_referrer: "",
      ga_client_id: "",
      ga_session_id: "",
    };
  }
  return {
    page_location: window.location.href.slice(0, 512),
    page_title: typeof document !== "undefined" ? document.title.slice(0, 300) : "",
    page_referrer: typeof document !== "undefined" ? document.referrer.slice(0, 420) : "",
    // Same id the owned beacon stamps on events_raw.client_id (the #860 join key).
    ga_client_id: getGoogleAnalyticsClientId() ?? "",
    // GA4 session id — stitches the server-side purchase to THIS browser session.
    ga_session_id: getGoogleAnalyticsSessionId() ?? "",
  };
}

export async function subscribe(data: {
  email?: string;
  phone?: string;
  moon_rashi?: string;
}): Promise<{ success: boolean; message: string }> {
  return apiRequest("/subscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function getPlans(language = "en"): Promise<PlansResponse> {
  const params = new URLSearchParams();
  if (language !== "en") params.set("language", language);
  const qs = params.toString();
  return apiRequest<PlansResponse>(`/plans${qs ? `?${qs}` : ""}`);
}

export async function getCheckoutUrl(data: {
  plan_id: string;
  plan_type: string;
  reading_id?: string;
  session_id?: string;
}): Promise<{ checkout_url: string; plan_id: string; plan_type: string }> {
  return apiRequest("/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function createPaymentOrder(data: {
  plan_id: string;
  plan_type: string;
  reading_id?: string;
  session_id?: string;
  email?: string;
  phone?: string;
  source?: string;
  placement?: string;
  intent?: string;
  source_path?: string;
  /** First-touch SEO cluster (kundli/panchang/dosha/...) captured on landing
   *  and persisted in sessionStorage. Threaded into Razorpay notes so paid
   *  orders attribute back to the cluster that drove discovery instead of
   *  the in-app path where the user happened to click "buy". */
  landing_cluster?: string;
  landing_path?: string;
  customer_problem?: string;
  intent_taxonomy_version?: string;
  landing_intent?: string;
  chat_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  intent_source?: string;
  paywall_variant?: string;
  question_intent?: string;
  chart_intent?: string;
  chat_signal_count?: number;
  strongest_dosha_id?: string;
  strongest_dosha_severity?: number;
  product_affinity?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  checkout_channel?: CheckoutChannel;
  commerce_system?: CommerceSystem;
  report_inputs?: Record<string, unknown>;
  ga_client_id?: string;
  /** Browser Pixel event UUID for Meta CAPI dedup. Generate with crypto.randomUUID()
   *  before firing the Pixel InitiateCheckout event and pass the same value here. */
  event_id?: string;
  /** Legacy backend field name. The value is now the first-party browser
   *  analytics distinct ID, round-tripped through Razorpay notes + audit_log
   *  for server-canonical commerce attribution. */
  posthog_distinct_id?: string;
}): Promise<CreateOrderResponse> {
  // Identity-resolution choke point: every checkout flows through here.
  // If the buyer gave us phone/email, attach an owned identity event and always
  // attach the browser distinct ID so Razorpay notes + audit_log carry it.
  const stableId = data.phone || data.email;
  if (stableId) {
    identifyUser(stableId, { email: data.email, phone: data.phone });
  }
  const enriched = {
    ...pageContext(),
    ...data,
    posthog_distinct_id: data.posthog_distinct_id ?? getAnalyticsDistinctId(),
    // Cloudflare Turnstile token (empty when the feature is dark / not solved).
    // Verified server-side; ignored entirely until Turnstile is enabled.
    turnstile_token: getTurnstileToken() ?? "",
  };
  return apiRequest<CreateOrderResponse>("/payment/create-order", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(enriched),
  });
}

export type AgenticCartShippingAddress = {
  first_name: string;
  last_name?: string;
  address1: string;
  address2?: string;
  city: string;
  province?: string;
  country_code?: string;
  zip: string;
  phone: string;
};

export async function getAgenticCartBundle(
  bundleId: string
): Promise<AgenticCartBundleSummaryResponse> {
  return apiRequest<AgenticCartBundleSummaryResponse>(
    `/payment/agentic-cart-bundles/${encodeURIComponent(bundleId)}`
  );
}

export async function createAgenticCartOrder(data: {
  bundle_id: string;
  reading_id?: string;
  session_id?: string;
  source_path?: string;
  intent?: string;
  customer_problem?: string;
  customer_email?: string;
  customer_phone?: string;
  shipping_address?: AgenticCartShippingAddress;
  amount_inr?: number;
  agent_trace_id?: string;
  selected_offer_reason?: string;
  ga_client_id?: string;
  event_id?: string;
  posthog_distinct_id?: string;
}): Promise<CreateAgenticCartOrderResponse> {
  const stableId = data.customer_phone || data.customer_email;
  if (stableId) {
    identifyUser(stableId, { email: data.customer_email, phone: data.customer_phone });
  }
  return apiRequest<CreateAgenticCartOrderResponse>("/payment/create-agentic-cart-order", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...pageContext(),
      ...data,
      posthog_distinct_id: data.posthog_distinct_id ?? getAnalyticsDistinctId(),
      turnstile_token: getTurnstileToken() ?? "",
    }),
  });
}

export async function verifyAgenticCartPayment(data: {
  cart_id: string;
  bundle_id: string;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  reading_id?: string;
  session_id?: string;
  customer_email?: string;
  customer_phone?: string;
  report_inputs?: Record<string, unknown>;
  source_path?: string;
  source?: string;
  placement?: string;
  intent?: string;
  customer_problem?: string;
  agent_trace_id?: string;
  selected_offer_reason?: string;
  ga_client_id?: string;
  event_id?: string;
  posthog_distinct_id?: string;
}): Promise<VerifyAgenticCartPaymentResponse> {
  return apiRequest<VerifyAgenticCartPaymentResponse>("/payment/verify-agentic-cart", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...pageContext(),
      ...data,
      posthog_distinct_id: data.posthog_distinct_id ?? getAnalyticsDistinctId(),
    }),
  });
}

export async function createMembershipSubscription(data: {
  plan_id: string;
  plan_type?: "subscription";
  reading_id?: string;
  session_id?: string;
  email?: string;
  phone?: string;
  source?: string;
  placement?: string;
  intent?: string;
  source_path?: string;
  checkout_channel?: CheckoutChannel;
  commerce_system?: CommerceSystem;
  ga_client_id?: string;
  posthog_distinct_id?: string;
}): Promise<CreateSubscriptionResponse> {
  const headers = await authJsonHeaders();
  const enriched = {
    ...pageContext(),
    ...data,
    plan_type: "subscription",
    posthog_distinct_id: data.posthog_distinct_id ?? getAnalyticsDistinctId(),
    turnstile_token: getTurnstileToken() ?? "",
  };
  return apiRequest<CreateSubscriptionResponse>("/payment/create-subscription", {
    method: "POST",
    headers,
    body: JSON.stringify(enriched),
  });
}

export async function verifyPayment(data: {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  plan_id: string;
  plan_type: string;
  reading_id?: string;
  session_id?: string;
  email?: string;
  phone?: string;
  source?: string;
  placement?: string;
  intent?: string;
  source_path?: string;
  customer_problem?: string;
  intent_taxonomy_version?: string;
  landing_intent?: string;
  chat_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  intent_source?: string;
  paywall_variant?: string;
  question_intent?: string;
  chart_intent?: string;
  chat_signal_count?: number;
  strongest_dosha_id?: string;
  strongest_dosha_severity?: number;
  product_affinity?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  checkout_channel?: string;
  commerce_system?: string;
  report_inputs?: Record<string, unknown>;
  ga_client_id?: string;
  /** Meta CAPI dedup: same event_id used for browser pixel + backend CAPI fire.
   *  Must match the event_id passed to createPaymentOrder. */
  event_id?: string;
  /** Legacy backend field name; value is the first-party browser analytics ID. */
  posthog_distinct_id?: string;
}): Promise<{
  success: boolean;
  credits_added: number;
  message: string;
  download_url?: string | null;
  report_job_id?: string | null;
  report_job_status?: string | null;
}> {
  const enriched = {
    ...pageContext(),
    ...data,
    posthog_distinct_id: data.posthog_distinct_id ?? getAnalyticsDistinctId(),
  };
  return apiRequest("/payment/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(enriched),
  });
}

export async function verifyMembershipSubscription(data: {
  razorpay_subscription_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  plan_id: string;
  email?: string;
  phone?: string;
  reading_id?: string;
  session_id?: string;
  source?: string;
  placement?: string;
  intent?: string;
  source_path?: string;
  checkout_channel?: string;
  commerce_system?: string;
  ga_client_id?: string;
}): Promise<VerifySubscriptionResponse> {
  const headers = await authJsonHeaders();
  return apiRequest<VerifySubscriptionResponse>("/payment/verify-subscription", {
    method: "POST",
    headers,
    body: JSON.stringify({ ...pageContext(), ...data }),
  });
}

export type ReportJobStatus = "queued" | "running" | "completed" | "failed";

export async function getReportJobStatus(jobId: string): Promise<{
  job_id: string;
  order_id: string;
  report_type: string;
  status: ReportJobStatus;
  attempts: number;
  max_attempts: number;
  queued_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  failed_at?: string | null;
  last_error?: string | null;
  download_url?: string | null;
}> {
  return apiRequest(`/reports/jobs/${encodeURIComponent(jobId)}`);
}

export type PaymentOrderStatus = {
  order_id: string;
  status: "activated" | "pending" | "unknown" | string;
  plan_type: string;
  plan_id: string;
  credits_added: number;
  download_url?: string | null;
  message: string;
};

export async function getPaymentOrderStatus(orderId: string): Promise<PaymentOrderStatus> {
  return apiRequest<PaymentOrderStatus>(`/payment/status/${encodeURIComponent(orderId)}`);
}

export async function abandonPayment(data: {
  razorpay_order_id: string;
  plan_id: string;
  plan_type: string;
  reading_id?: string;
  session_id?: string;
  email?: string;
  phone?: string;
  source?: string;
  placement?: string;
  checkout_channel?: string;
  commerce_system?: string;
  intent?: string;
  source_path?: string;
  customer_problem?: string;
  intent_taxonomy_version?: string;
  landing_intent?: string;
  chat_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  intent_source?: string;
  paywall_variant?: string;
  question_intent?: string;
  chart_intent?: string;
  chat_signal_count?: number;
  strongest_dosha_id?: string;
  strongest_dosha_severity?: number;
  product_affinity?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  recovery_channel?: string;
  recovery_url?: string;
  recovery_email_consent?: boolean;
  recovery_phone_consent?: boolean;
  ga_client_id?: string;
}): Promise<{ success: boolean }> {
  return apiRequest("/payment/abandon", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...pageContext(), ...data }),
  });
}

export async function recordPaymentFailure(data: {
  razorpay_order_id: string;
  plan_id?: string;
  plan_type?: string;
  source?: string;
  placement?: string;
  intent?: string;
  source_path?: string;
  customer_problem?: string;
  intent_taxonomy_version?: string;
  landing_intent?: string;
  chat_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  intent_source?: string;
  paywall_variant?: string;
  question_intent?: string;
  chart_intent?: string;
  chat_signal_count?: number;
  strongest_dosha_id?: string;
  strongest_dosha_severity?: number;
  product_affinity?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  checkout_channel?: string;
  commerce_system?: string;
  agent_trace_id?: string;
  error_code?: string;
  error_description?: string;
  error_source?: string;
  error_step?: string;
  error_reason?: string;
  payment_id?: string;
  method?: string;
  ga_client_id?: string;
}): Promise<{ success: boolean }> {
  return apiRequest("/payment/failure", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...pageContext(), ...data }),
  });
}

/**
 * Reading-level accuracy rating — the calibration signal (rule 44). Best-effort:
 * the backend soft-fails, and the caller treats any error as a no-op (the seeker
 * already has their reading). reading_id is the report's order_id.
 */
export async function rateReading(
  orderId: string,
  data: { thumbs?: 1 | -1; stars?: number; comment?: string }
): Promise<{ ok: boolean }> {
  return apiRequest(`/reports/${encodeURIComponent(orderId)}/rate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export function getReportDownloadUrl(downloadPath: string): string {
  if (/^https?:\/\//.test(downloadPath)) {
    return downloadPath;
  }
  const path = downloadPath.startsWith("/") ? downloadPath : `/${downloadPath}`;
  const accessPrefix = "/reports/access/";
  if (path.startsWith(accessPrefix)) {
    return `https://ishvaram.com/r/${encodeURIComponent(path.slice(accessPrefix.length))}`;
  }
  return `${API_BASE}${path}`;
}
